import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useBlockUser } from "@/hooks/useBlockUser";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, ShieldOff } from "lucide-react";

interface ChatBlockingStatusProps {
  partnerId: string;
  onBlockStatusChange?: (isBlocked: boolean) => void;
}

export const ChatBlockingStatus = ({ partnerId, onBlockStatusChange }: ChatBlockingStatusProps) => {
  const { user } = useAuth();
  const { checkIfBlocked, unblockUser, loading } = useBlockUser();
  const [blockStatus, setBlockStatus] = useState<{
    userBlocked: boolean;
    partnerBlocked: boolean;
  }>({ userBlocked: false, partnerBlocked: false });
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (user && partnerId) {
      checkBlockingStatus();
    }
  }, [user, partnerId]);

  const checkBlockingStatus = async () => {
    if (!user || !partnerId) return;

    try {
      setLoadingStatus(true);
      
      const [userBlocked, partnerBlocked] = await Promise.all([
        checkIfBlocked(partnerId), // Check if current user blocked the partner
        supabase.from('user_blocks')
          .select('id')
          .eq('blocker_id', partnerId)
          .eq('blocked_id', user.id)
          .maybeSingle()
          .then(({ data }) => !!data) // Check if partner blocked current user
      ]);

      setBlockStatus({ userBlocked, partnerBlocked });
      onBlockStatusChange?.(userBlocked || partnerBlocked);
    } catch (error) {
      console.error('Error checking blocking status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleUnblock = async () => {
    if (!blockStatus.userBlocked) return;

    const success = await unblockUser(partnerId);
    if (success) {
      setBlockStatus(prev => ({ ...prev, userBlocked: false }));
      onBlockStatusChange?.(false);
    }
  };

  if (loadingStatus) return null;

  // Show if user has blocked the partner
  if (blockStatus.userBlocked) {
    return (
      <Card className="bg-red-950/30 border-red-700/50 mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-sm font-medium text-red-300">You have blocked this user</p>
                <p className="text-xs text-red-400/70">You cannot send or receive messages</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleUnblock}
              disabled={loading}
              className="border-red-600 text-red-300 hover:bg-red-700/20"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Unblock
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show if partner has blocked the user
  if (blockStatus.partnerBlocked) {
    return (
      <Card className="bg-orange-950/30 border-orange-700/50 mb-4">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-sm font-medium text-orange-300">This user has blocked you</p>
              <p className="text-xs text-orange-400/70">You cannot send messages to this user</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};